import { Branch } from './../components/constants/branchData';
import { useState, useEffect, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useBranches } from "src/app/lib/fetchBranches";
import { useFetchBranch } from "src/app/lib/useFetchBranch";
import { extractIdFromSlug, generateSlugWithId } from "src/app/serverUtils";
import { BranchType } from "../components/templates/design1/components";

interface UseBranchSelectionResult {
    selectedValue: string;
    setSelectedValue: React.Dispatch<React.SetStateAction<string>>;
    branchOptions: { value: string; label: string }[];
    branchDetails: BranchType | undefined;
    loading: boolean;
    handleSelect: (value: string) => void;
    branchIdFromUrl: string;
    extractedId: string | number;
    branches?: Branch[]
}

/**
 * Custom hook to manage branch selection, URL syncing, and fetching branch details.
 */
export function useBranchSelection(initialData?: BranchType): UseBranchSelectionResult {
    const searchParams = useSearchParams();
    const router = useRouter();
    // Fetch all branches
    const { data: branches } = useBranches();
    // Extract branch options for select
    const branchOptions = useMemo(
        () =>
            branches?.map((branch) => ({
                value: String(branch.id),
                label: branch.branchName,
            })) || [],
        [branches]
    );

    // Get branch id from URL or fallback to first branch
    const branchIdFromUrl = searchParams.get("branch") || (branches?.[0]?.id ?? "").toString();

    // Extract numeric ID from slug string
    const extractedId = extractIdFromSlug(branchIdFromUrl);

    // Fetch branch details using extracted ID
    const { data: branchDetails, isPending, isRefetching, isLoading } = useFetchBranch(
        Number(extractedId),
        initialData
    );

    // Loading state
    const loading = isPending || isRefetching || isLoading;

    // Manage selectedValue state for select input
    const [selectedValue, setSelectedValue] = useState<string>(branchIdFromUrl);

    // Sync selectedValue with URL param & available branchOptions
    useEffect(() => {
        if (extractedId && branchOptions.length > 0) {
            const selectedBranch = branchOptions.find(
                (item) => item.value === String(extractedId)
            );
            setSelectedValue(selectedBranch?.value || branchOptions[0].value);
        }
    }, [branchDetails, branchOptions, extractedId]);

    // Handle select value change
    const handleSelect = (value: string) => {
        setSelectedValue(value);
        const selectedBranch = branchOptions.find((item) => item.value === value);
        if (!selectedBranch) return;
        const slugWithId = generateSlugWithId(Number(value), selectedBranch.label);
        router.push(`?branch=${slugWithId}`, { scroll: false });
    };

    return {
        selectedValue,
        setSelectedValue,
        branchOptions,
        branchDetails,
        loading,
        branches,
        handleSelect,
        branchIdFromUrl,
        extractedId,
    };
}
